'use client'

import { FormEvent, useId, useMemo, useState } from 'react'

import { AddressAutocompleteInput } from '@/components/campaigns/address-autocomplete-input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { InputGroup } from '@/components/ui/input-group'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import ButtonBlock, { type ButtonBlockData } from '@/payload/blocks/button-group/button-block'

import { verifyRegistrationAction } from '@/payload/blocks/verify-registration/actions'
import { US_STATE_CODES } from '@/lib/voter-file/states'
import type { VoterFileRow } from '@/lib/voter-file'

type Step = 'form' | 'select' | 'registered' | 'not-found'

type FormState = {
  firstName: string
  lastName: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  lat: number | null
  lng: number | null
  email: string
  phone: string
  smsOptIn: boolean
  emailOptIn: boolean
}

const INITIAL_FORM_STATE: FormState = {
  firstName: '',
  lastName: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  lat: null,
  lng: null,
  email: '',
  phone: '',
  smsOptIn: false,
  emailOptIn: false,
}

function formatVoterName(row: VoterFileRow): string {
  const parts = [row.name_first, row.name_middle, row.name_last, row.name_suffix].filter(Boolean)
  return parts.join(' ').trim() || 'Voter name'
}

function formatLocationLine(row: VoterFileRow): string {
  const cityStateZip = [
    row.addr_city_residential,
    [row.addr_state_residential, row.addr_zip_residential].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
  return cityStateZip || 'City, ST ZIP'
}

/**
 * Formats `dt_last_registration_update` for display, or `null` when the
 * value is missing or out of a sane range (voter-file dates are dirty at the
 * edges — see `src/lib/unified/data-dictionary.ts`).
 */
function formatLastUpdated(value: string | null | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const year = date.getUTCFullYear()
  if (year < 1900 || date.getTime() > Date.now()) return null
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

function VoterRecordRow({ row }: { row: VoterFileRow }) {
  return (
    <div className="min-w-0">
      <p>{formatVoterName(row)}</p>
      <p>{row.addr_address_residential?.trim() || 'Address unavailable'}</p>
      {row.addr_address2_residential?.trim() ? <p>{row.addr_address2_residential}</p> : null}
      <p>{formatLocationLine(row)}</p>
    </div>
  )
}

export type VerifyRegistrationClientProps = {
  buttonsIfFound?: ButtonBlockData[]
  buttonsIfNotFound?: ButtonBlockData[]
  everyAction: {
    enabled: boolean
    showSmsOptIn: boolean
    showEmailOptIn: boolean
    smsOptInLabel: string
    emailOptInLabel: string
  }
}

export default function VerifyRegistrationClient({
  buttonsIfFound = [],
  buttonsIfNotFound = [],
  everyAction,
}: VerifyRegistrationClientProps) {
  const baseId = useId()
  const [step, setStep] = useState<Step>('form')
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [matches, setMatches] = useState<VoterFileRow[]>([])
  const [selectedMatch, setSelectedMatch] = useState<VoterFileRow | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const foundButtons = useMemo<ButtonBlockData[]>(
    () =>
      Array.isArray(buttonsIfFound)
        ? buttonsIfFound.filter((b): b is ButtonBlockData => Boolean(b))
        : [],
    [buttonsIfFound],
  )
  const notFoundButtons = useMemo<ButtonBlockData[]>(
    () =>
      Array.isArray(buttonsIfNotFound)
        ? buttonsIfNotFound.filter((b): b is ButtonBlockData => Boolean(b))
        : [],
    [buttonsIfNotFound],
  )

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [key]: value }))
  }

  const resetFlow = () => {
    setStep('form')
    setFormState(INITIAL_FORM_STATE)
    setMatches([])
    setSelectedMatch(null)
    setSubmitError(null)
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return
    setSubmitError(null)

    if (!formState.city.trim()) {
      setSubmitError('Please enter your city.')
      return
    }
    if (!formState.state) {
      setSubmitError('Please select your state.')
      return
    }
    if (!/^\d{5}(-?\d{4})?$/.test(formState.zip.trim())) {
      setSubmitError('Please enter a valid ZIP code.')
      return
    }

    if (everyAction.enabled && everyAction.showSmsOptIn && formState.smsOptIn) {
      const digits = formState.phone.replace(/\D/g, '')
      if (digits.length < 10) {
        setSubmitError('Please enter a valid phone number for SMS opt-in.')
        return
      }
    }

    if (everyAction.enabled && everyAction.showEmailOptIn && formState.emailOptIn) {
      const email = formState.email.trim().toLowerCase()
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setSubmitError('Please enter a valid email address for email opt-in.')
        return
      }
    }

    setIsSubmitting(true)

    try {
      const everyActionPayload =
        everyAction.enabled &&
        (everyAction.showSmsOptIn || everyAction.showEmailOptIn) &&
        (formState.smsOptIn || formState.emailOptIn)
          ? {
              smsOptIn: everyAction.showSmsOptIn ? formState.smsOptIn : false,
              emailOptIn: everyAction.showEmailOptIn ? formState.emailOptIn : false,
              phone: everyAction.showSmsOptIn && formState.smsOptIn ? formState.phone : undefined,
              email: everyAction.showEmailOptIn && formState.emailOptIn ? formState.email : undefined,
            }
          : undefined

      const result = await verifyRegistrationAction({
        firstName: formState.firstName,
        lastName: formState.lastName,
        address: formState.address,
        address2: formState.address2 || undefined,
        city: formState.city,
        state: formState.state,
        zip: formState.zip,
        everyAction: everyActionPayload,
      })

      if (!result.ok) {
        setSubmitError(result.message)
        return
      }

      setMatches(result.matches)
      if (result.matches.length === 0) {
        setStep('not-found')
        return
      }
      if (result.matches.length === 1) {
        setSelectedMatch(result.matches[0])
        setStep('registered')
        return
      }
      setStep('select')
    } catch (error) {
      console.error('VerifyRegistration submit failed', error)
      setSubmitError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const registrationStatus =
    selectedMatch?.civitech_voter_status?.trim() ||
    selectedMatch?.sos_voter_status?.trim() ||
    'Registered'

  const lastUpdatedLabel = formatLastUpdated(selectedMatch?.dt_last_registration_update)

  return (
    <>
      {step === 'form' ? (
        <form className="flex flex-col gap-theme-md w-full" onSubmit={onSubmit} noValidate>
          <Card>
            <CardContent className="flex flex-col gap-theme-md p-6 md:p-8">
              <div className="grid gap-theme-md sm:grid-cols-2">
                <InputGroup>
                  <label htmlFor={`${baseId}-first`} className="theme-label">
                    First name
                  </label>
                  <Input
                    id={`${baseId}-first`}
                    required
                    autoComplete="given-name"
                    placeholder="Jane"
                    value={formState.firstName}
                    onChange={(event) => updateForm('firstName', event.target.value)}
                  />
                </InputGroup>
                <InputGroup>
                  <label htmlFor={`${baseId}-last`} className="theme-label">
                    Last name
                  </label>
                  <Input
                    id={`${baseId}-last`}
                    required
                    autoComplete="family-name"
                    placeholder="Doe"
                    value={formState.lastName}
                    onChange={(event) => updateForm('lastName', event.target.value)}
                  />
                </InputGroup>
              </div>

              <InputGroup>
                <label htmlFor={`${baseId}-address`} className="theme-label">
                  Address
                </label>
                <AddressAutocompleteInput
                  id={`${baseId}-address`}
                  required
                  value={formState.address}
                  onValueChange={(value) => {
                    updateForm('address', value)
                  }}
                  onPlaceSelected={(place) => {
                    updateForm('address', place.address)
                    updateForm('city', place.city)
                    updateForm('state', place.state)
                    updateForm('zip', place.zip)
                    updateForm('lat', place.lat)
                    updateForm('lng', place.lng)
                  }}
                />
              </InputGroup>

              <InputGroup>
                <label htmlFor={`${baseId}-address2`} className="theme-label">
                  Apartment, suite, etc.
                  <span className="ml-1 text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id={`${baseId}-address2`}
                  autoComplete="address-line2"
                  placeholder="Apt 4B"
                  value={formState.address2}
                  onChange={(event) => updateForm('address2', event.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <label htmlFor={`${baseId}-city`} className="theme-label">
                  City
                </label>
                <Input
                  id={`${baseId}-city`}
                  required
                  autoComplete="address-level2"
                  placeholder="City"
                  value={formState.city}
                  onChange={(event) => updateForm('city', event.target.value)}
                />
              </InputGroup>

              <div className="grid gap-theme-md sm:grid-cols-2">
                <InputGroup>
                  <label htmlFor={`${baseId}-state`} className="theme-label">
                    State
                  </label>
                  <select
                    id={`${baseId}-state`}
                    required
                    autoComplete="address-level1"
                    value={formState.state}
                    onChange={(event) => updateForm('state', event.target.value)}
                    className={cn(
                      'theme-input w-full min-w-0 appearance-none bg-[image:var(--theme-select-chevron,none)] bg-[length:1rem_1rem] bg-[position:right_0.75rem_center] bg-no-repeat pr-9 transition-[color,box-shadow] outline-none',
                      'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                      'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
                    )}
                  >
                    <option value="" disabled>
                      Select a state
                    </option>
                    {US_STATE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </InputGroup>
                <InputGroup>
                  <label htmlFor={`${baseId}-zip`} className="theme-label">
                    ZIP code
                  </label>
                  <Input
                    id={`${baseId}-zip`}
                    required
                    autoComplete="postal-code"
                    inputMode="numeric"
                    placeholder="12345"
                    maxLength={10}
                    value={formState.zip}
                    onChange={(event) => updateForm('zip', event.target.value)}
                  />
                </InputGroup>
              </div>

              {everyAction.enabled && everyAction.showSmsOptIn ? (
                <InputGroup>
                  <label htmlFor={`${baseId}-phone`} className="theme-label">
                    Phone
                    {!formState.smsOptIn ? (
                      <span className="ml-1 text-muted-foreground">(optional)</span>
                    ) : null}
                  </label>
                  <Input
                    id={`${baseId}-phone`}
                    type="tel"
                    autoComplete="tel"
                    placeholder="(555) 555-5555"
                    value={formState.phone}
                    onChange={(event) => updateForm('phone', event.target.value)}
                    required={formState.smsOptIn}
                  />
                </InputGroup>
              ) : null}

              {everyAction.enabled && everyAction.showEmailOptIn ? (
                <InputGroup>
                  <label htmlFor={`${baseId}-email`} className="theme-label">
                    Email
                    {!formState.emailOptIn ? (
                      <span className="ml-1 text-muted-foreground">(optional)</span>
                    ) : null}
                  </label>
                  <Input
                    id={`${baseId}-email`}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={formState.email}
                    onChange={(event) => updateForm('email', event.target.value)}
                    required={formState.emailOptIn}
                  />
                </InputGroup>
              ) : null}

              {everyAction.enabled && (everyAction.showSmsOptIn || everyAction.showEmailOptIn) ? (
                <div className="space-y-3 border-t pt-4">
                  {everyAction.showSmsOptIn ? (
                    <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formState.smsOptIn}
                        onChange={(event) => updateForm('smsOptIn', event.target.checked)}
                        className="mt-0.5 size-4 shrink-0"
                        style={{ accentColor: 'var(--button-primary-background)' }}
                      />
                      <span>{everyAction.smsOptInLabel}</span>
                    </label>
                  ) : null}
                  {everyAction.showEmailOptIn ? (
                    <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formState.emailOptIn}
                        onChange={(event) => updateForm('emailOptIn', event.target.checked)}
                        className="mt-0.5 size-4 shrink-0"
                        style={{ accentColor: 'var(--button-primary-background)' }}
                      />
                      <span>{everyAction.emailOptInLabel}</span>
                    </label>
                  ) : null}
                </div>
              ) : null}

              {submitError ? (
                <p role="alert" style={{ color: 'var(--color-theme-button-destructive-background)' }}>
                  {submitError}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Checking…' : 'Check registration'}
          </Button>
        </form>
      ) : (
        <Card className="w-full">
          <CardContent className="p-6 md:p-8">
            {step === 'select' ? (
            <div className="space-y-5">
              <div className="space-y-1">
                <h3>Select your voter record</h3>
                <p>We found {matches.length} possible matches. Select the one that looks like you.</p>
              </div>
              <div className="flex flex-col gap-3">
                {matches.map((row, index) => (
                  <button
                    key={`${row.id_sos_civitech}-${row.state}-${index}`}
                    type="button"
                    className="flex w-full items-start justify-between gap-3 rounded-md border p-4 text-left transition hover:bg-muted/40"
                    onClick={() => {
                      setSelectedMatch(row)
                      setStep('registered')
                    }}
                  >
                    <VoterRecordRow row={row} />
                    <span aria-hidden className="text-muted-foreground">
                      →
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-3">
                <Button type="button" variant="outline" className="w-full" onClick={() => setStep('not-found')}>
                  None of these are me
                </Button>
                <button
                  type="button"
                  className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => setStep('form')}
                >
                  Back
                </button>
              </div>
            </div>
          ) : null}

          {step === 'registered' && selectedMatch ? (
            <div className="space-y-5">
              <div className="space-y-1">
                <h3>You&apos;re currently registered to vote</h3>
                <p>Here&apos;s the voter record we matched with your information.</p>
              </div>
              <div className="flex items-start justify-between gap-3 rounded-md border p-4">
                <VoterRecordRow row={selectedMatch} />
                <span className="shrink-0 rounded-full border bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  {registrationStatus}
                </span>
              </div>
              {lastUpdatedLabel ? (
                <p className="text-sm text-muted-foreground">Registration last updated {lastUpdatedLabel}</p>
              ) : null}

              <button
                type="button"
                className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
                onClick={resetFlow}
              >
                Search again
              </button>
            </div>
          ) : null}

          {step === 'not-found' ? (
            <div className="space-y-5">
              <div className="space-y-2">
                <h3>No results found</h3>
                <p>
                  We couldn&apos;t find you based on the information you provided. Here are a few
                  possible reasons why:
                </p>
                <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
                  <li>You aren&apos;t registered to vote</li>
                  <li>Different first or last name spelling</li>
                  <li>Old or incorrect address</li>
                  <li>You recently registered to vote</li>
                </ul>
              </div>

              <Button type="button" variant="outline" className="w-full" onClick={resetFlow}>
                Search again
              </Button>
            </div>
          ) : null}
          </CardContent>
        </Card>
      )}

      {step === 'registered' && selectedMatch
        ? foundButtons.map((button, index) => (
            <ButtonBlock
              key={button.id ?? `found-${index}`}
              button={button}
              className="w-full"
            />
          ))
        : null}

      {step === 'not-found'
        ? notFoundButtons.map((button, index) => (
            <ButtonBlock
              key={button.id ?? `not-found-${index}`}
              button={button}
              className="w-full"
            />
          ))
        : null}
    </>
  )
}
